<!-- EnvironmentCheckScreen.vue -->
<script setup lang="ts">
    import '../../../css/choice.css';
    import { reactive, ref, nextTick, onMounted, computed } from 'vue';
    import { ChoiceApi } from '../../LibMain';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { SavedEnvironmentCheckDataModel, EnvironmentCheckMetricsModel, EnvironmentCheckPercentiles } from '../../lib/Api/CustomerSupport';
    import { ICol, RenderModeType, FormattedValueStr } from '../../lib/ChoiceTableHelpers';
    import { DisplayStyle, TableRowType, IconTypes } from '../../Enums/UiEnums';
    import { currentChoiceUser, currentChoiceDatabase, timeout, isBangUser } from '../../lib/ChoiceUtils';
    import { initPinia } from '../../lib/PiniaMgr';
    import { useListStore } from '../../lib/Stores/ListStore';
    import { IUserIdListItemModel } from '../../lib/Api/List';
    import { convertToIPv4IfPossible } from '../../lib/Utils/IpAddress';
    import { ProductType } from '../../Enums/ChoiceEnums';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import ChoiceTable from '../../components/ChoiceTable.vue';
    import ChoiceTableCell from '../../components/Table/ChoiceTableCell.vue';
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue';
    import MessageBadge from '../../components/Messages/MessageBadge.vue';
    import MessageSection from '../../components/Messages/MessageSection.vue';
    import EnvironmentCheckTestRunner from './EnvironmentCheckTestRunner.vue';
    import DropDownButtonItemsWithFilter from '../../components/Buttons/DropDownButtonItemsWithFilter.vue';
    import EnvironmentCheckPercentileValue from './EnvironmentCheckPercentileValue.vue';
    import ChoiceButton from '../../components/Buttons/ChoiceButton.vue';
    import Icon from '../../components/Icon.vue';
    import ApiPerformanceResults from './CustomerSupport/Telemetry/ApiPerformanceResults.vue'
    import FoldingFieldset from '../../components/FoldingFieldset.vue';
    import FoldingPanel from '../../components/FoldingPanel.vue';
    import PageErrorLog from './CustomerSupport/Telemetry/PageErrorLog.vue'

    const compData = reactive<{
        runningTests?: boolean,
        testRunnerKey?: number,
        userIds?: IUserIdListItemModel[],
        resultsUserId?: string,
        environmentCheckDataResultsOpen?: boolean,
        environmentCheckData?: SavedEnvironmentCheckDataModel[],
        globalMetrics?: EnvironmentCheckMetricsModel,
        companyMetrics?: EnvironmentCheckMetricsModel,
        serverDate?: Date,
        clientDateBefore?: Date,
        clientDateAfter?: Date,
        isBangUser?: boolean,
        telemetryPanelOpen?: boolean,
        apiPerformanceResultsPanelOpen?: boolean,
        pageErrorPanelOpen?: boolean,
        performanceAuditEnabled?: boolean
    }>({
        environmentCheckDataResultsOpen: true,
        telemetryPanelOpen: false,
        apiPerformanceResultsPanelOpen: false,
        pageErrorPanelOpen: false
    });

    const resultsColumns: ICol[] =
        [
            { Name: "Type", DataField: "description", ExtraData: "Type" },
            { Name: "Computation Test", DataField: "fibonacciTestTime", ExtraData: "Metrics" },
            { Name: "Rendering Test", DataField: "domTestTime", ExtraData: "Metrics" },
            { Name: "3rd Party Download Test", DataField: "downloadThirdPartyTime", ExtraData: "Metrics" },
            { Name: "OfficeEMR Download Test", DataField: "downloadOurServerTime", ExtraData: "Metrics"},
            //{ Name: "IP Address", DataField: "ipAddress", FormatBodyValue: toIpV4 },
            { Name: "Run Timestamp", DataField: "timestamp", RenderMode: RenderModeType.DateTime },
        ];

    const numberOfDaysOfResults: number = 30;
    const testRunner = ref();
    const listStore = useListStore(initPinia());

    const apiPerformanceResults = ref<InstanceType<typeof ApiPerformanceResults>>();

    onMounted(() =>{

        if (isBangUser())
        {
            compData.isBangUser = true;

            listStore.getAllUserIds()
                .then(userIds => compData.userIds = userIds);
        }
        ChoiceApi.customerSupport.getEnvironmentCheckGlobalMetrics()
            .then(resp => compData.globalMetrics = resp);
        ChoiceApi.customerSupport.getEnvironmentCheckCompanyMetrics()
            .then(resp => compData.companyMetrics = resp);
        
        // This gets the client Datetime before asking the server for their date time
        compData.clientDateBefore = new Date();
        //Gets server Datetime
        ChoiceApi.customerSupport.GetDate()
            .then(resp => {
                if (!resp) {
                    return;
                }
                compData.serverDate = new Date(resp);
                // This gets the client Datetime after asking the server for their date time
                compData.clientDateAfter = new Date();
            });

        ChoiceApi.companySettings.isProductActive(ProductType.PerformanceAudit)
            .then(resp => {
                if (true === resp){
                    compData.performanceAuditEnabled = true;
                }
            });
    });
    const globalMetricsDataRow = computed(() => getMetricsDataModel(compData.globalMetrics ?? {}, "Global Median"));
    const companyMetricsDataRow = computed(() => getMetricsDataModel(compData.companyMetrics ?? {}, "Company Median"));

    function getMetricsDataModel(metrics: EnvironmentCheckMetricsModel, desc: string): ResultsFooterRow
    {
        return {
            fibonacciTestTime: metrics.fibonacciTestTime?.median,
            domTestTime: metrics.domTestTime?.median,
            downloadOurServerTime: metrics.downloadOurServerTime?.median,
            downloadThirdPartyTime: metrics.downloadThirdPartyTime?.median,
            description: desc,
            countOfResults: metrics.countOfResults,
            metrics: true
        };
    }

    async function runTests()
    {
        compData.testRunnerKey = (compData.testRunnerKey ?? 0) + 1;
        compData.runningTests = true;
        await nextTick();

        // Linger while waiting for the run to complete -- run will return immediately despite being an async function
        testRunner.value.run();
        while(true)
            {
            await timeout(1000);
            if (!testRunner.value.runExecuting)
                break;
        }

        compData.runningTests = false;

        await showEnvironmentCheckResults();
    }

    const timeSynced = computed(() => {
        if (!compData.clientDateBefore || !compData.clientDateAfter || !compData.serverDate)
            return false;
        const ThirtySecsMinusBefClientDate = new Date(compData.clientDateBefore.getTime() - 30 * 1000);
        const ThirtySecsPlusAftClientDate = new Date(compData.clientDateAfter.getTime() + 30 * 1000);
        if (compData.serverDate < ThirtySecsMinusBefClientDate) {
            console.log(`Client Time Before : ${compData.clientDateBefore}\nServer Time : ${compData.serverDate}\nClient Time After : ${compData.clientDateAfter}`);
            return false;
        }
        if (compData.serverDate > ThirtySecsPlusAftClientDate) {
            console.log(`Client Time Before : ${compData.clientDateBefore}\nServer Time : ${compData.serverDate}\nClient Time After : ${compData.clientDateAfter}`);
            return false;
        }
        return true;
    });
    async function showEnvironmentCheckResults(userId?: string) {
        compData.environmentCheckData = undefined;
        apiPerformanceResults.value?.clearData();
        compData.resultsUserId = userId;
        compData.environmentCheckData = await ChoiceApi.customerSupport.getEnvironmentCheckResults(numberOfDaysOfResults, userId);
    }

    const noResultsData = computed<boolean>(() => 0 === (compData.environmentCheckData?.length ?? 0));
    const footerData = computed(()=> [companyMetricsDataRow.value, globalMetricsDataRow.value]);

    interface ResultsRow extends SavedEnvironmentCheckDataModel
    {
        description?: string
        metrics?: boolean
    }
    interface ResultsFooterRow extends ResultsRow
    {
        countOfResults?: number
    }

    function rowClass(row: Record<string, any>): string|undefined
    {
        if (row["metrics"])
            return "HeaderFooter";
    }

    function toIpV4(value: any, col: ICol, row: any)
    {
        return convertToIPv4IfPossible(value);
    }

    function getPercential(value: any, col:  ICol): EnvironmentCheckPercentiles | undefined
    {
        switch (col.DataField)
            {
            case "fibonacciTestTime":
                return compData.globalMetrics?.fibonacciTestTime;

            case "domTestTime":
                return compData.globalMetrics?.domTestTime;

            case "downloadOurServerTime":
                return compData.globalMetrics?.downloadOurServerTime;

            case "downloadThirdPartyTime":
                return compData.globalMetrics?.downloadThirdPartyTime;
        }
    }

    const resultsTableTypeColumnValue = computed(() =>{
        return compData.resultsUserId ?? currentChoiceUser();
    });

    const performanceAuditEnabledMsg = computed<string>(() => {
        return `Performance Auditing ${compData.performanceAuditEnabled ? "Enabled" : "Disabled"}`;
    });

</script>

<style scoped>
    .top-right 
    {
        position: fixed;
        top: 0;
        right: 0;
    }
</style>

<template>
    <div class="isalus-page-title">
        Customer Support Details
    </div>
    <div class="mb-1">
        <MessageBadge>
            User: {{ currentChoiceUser() }}
        </MessageBadge>
        <MessageBadge>
            Database: {{ currentChoiceDatabase() }}
        </MessageBadge>
    </div>

    <span class="me-1">
        <ActionButtonV2 :action="ActionButtonStyle.Save" text="Run" :performAction="runTests" />
    </span>
    <span class="me-1">
        <DropDownSplitButton @onButtonClick="showEnvironmentCheckResults" v-if="compData.userIds">
            <template #button>
                Results
            </template>
            <DropDownButtonItemsWithFilter :items="compData.userIds" :toFilterText="(item) => [item.users_id, item.userText].join(' ')"
                @itemClicked="item => showEnvironmentCheckResults(item.users_id)">
                <template #item="{item}">
                    {{ item.users_id }} ({{ item.userText  }})
                    <MessageBadge v-if="!item.active" :displayStyle="DisplayStyle.Danger">
                        Inactive
                    </MessageBadge>
                </template>
            </DropDownButtonItemsWithFilter>
        </DropDownSplitButton>

        <ChoiceButton v-if="!compData.userIds" @click="showEnvironmentCheckResults()" :action="ActionButtonStyle.NoIcon">
            Results
        </ChoiceButton>
    </span>

    <EnvironmentCheckTestRunner ref="testRunner" :key="compData.testRunnerKey"/>

    <div class="mb-1" v-if="compData.resultsUserId">
        <MessageBadge :displayStyle="DisplayStyle.Info">
            Filtered User: {{ compData.resultsUserId }}
        </MessageBadge>
    </div>

    <div v-if="compData.isBangUser && compData.environmentCheckData" class="mt-2">
        <FoldingPanel v-model:panelOpen="compData.telemetryPanelOpen">
            <template #title>Telemetry</template>
            <template #body>
                <FoldingFieldset title="Degraded API Results" v-model:panelOpen="compData.apiPerformanceResultsPanelOpen">
                    <div class="text-end">
                        <MessageBadge>
                            {{  performanceAuditEnabledMsg }}
                        </MessageBadge>
                    </div>
                    <ApiPerformanceResults 
                            ref="apiPerformanceResults" 
                            :resultsUserId="compData.resultsUserId"
                            @newDataLoaded="compData.environmentCheckDataResultsOpen = false" />
                </FoldingFieldset>
                <FoldingFieldset title="Page Errors" v-model:panelOpen="compData.pageErrorPanelOpen">
                    <PageErrorLog :userId="compData.resultsUserId" :numberOfDaysOfResults="numberOfDaysOfResults" />
                </FoldingFieldset>
            </template>
        </FoldingPanel>
    </div>

    <div class="mt-2">
        <FoldingPanel v-if="undefined !== compData.environmentCheckData" v-model:panelOpen="compData.environmentCheckDataResultsOpen">
            <template #title>
                Test Results
            </template>
            <template #body>
                <ChoiceTable 
                    v-if="!noResultsData"
                    :columns="resultsColumns" 
                    :data="compData.environmentCheckData"
                    :rowBodyClass="rowClass"
                    :footerVals="footerData"
                    :showFooter="true"
                    :allowSorting="true"
                    keyValueDataMember="timestamp">
                    <template #cell="{value, col, row}">
                        <template v-if="'Metrics' === col.ExtraData">
                            <EnvironmentCheckPercentileValue :value="value" :percentiles="getPercential(value, col)" />
                        </template>
                        <template v-else-if="'Type' === col.ExtraData">
                            {{ resultsTableTypeColumnValue }}
                        </template>
                        <template v-else>
                            <ChoiceTableCell :col="col" :row="row" />
                        </template>

                        <template v-if="'downloadThirdPartyTime' === col.DataField">
                            <MessageBadge :displayStyle="DisplayStyle.Danger" v-if="row.downloadThirdPartyFailureCount" title="The number of download attempt failures.  A max of 10 attempts is made.">
                                {{ row.downloadThirdPartyFailureCount }} Failures
                            </MessageBadge>
                        </template>
                        <template v-if="'downloadOurServerTime' === col.DataField">
                            <MessageBadge :displayStyle="DisplayStyle.Danger" v-if="row.downloadOurServerFailureCount" title="The number of download attempt failures.  A max of 10 attempts is made.">
                                {{ row.downloadOurServerFailureCount}} Failures                                
                            </MessageBadge>
                        </template>

                    </template>
                    <template #footer="{col, row}">
                        <template v-if="'Metrics' === col.ExtraData">
                            <span :title="`${row['countOfResults']} samples`">
                                {{ FormattedValueStr(col, row, TableRowType.Footer) }}
                            </span>
                        </template>
                        <template v-else>
                            {{FormattedValueStr(col, row, TableRowType.Footer)}}
                        </template>
                    </template>
                </ChoiceTable>

                <MessageSection v-if="noResultsData" :displayStyle="DisplayStyle.Warning">
                    There are no results for this user ({{ compData.resultsUserId ?? currentChoiceUser() }}) for the last {{ numberOfDaysOfResults }} days
                </MessageSection>

                <em class="text-muted">
                    All test times are in milli-seconds.
                    Run Timestamp is displayed using your computer time zone.
                    Data is from the last {{ numberOfDaysOfResults }} days of results.
                </em>
            </template>
        </FoldingPanel>
    </div>

    <div>
        <div v-if="compData.serverDate" class="top-right">
            <div v-if="timeSynced" >
                <span class="me-1">
                    <MessageBadge :display-style="DisplayStyle.Success">
                        <span class="me-1">
                            <Icon :icon="IconTypes.CheckMark" :displayStyle="DisplayStyle.Light" />
                        </span>
                        Client and Server Clocks are synced
                    </MessageBadge>
                </span>
            </div>
            <div v-else>
                <span class="me-1">
                    <MessageBadge :display-style="DisplayStyle.Danger">
                        <span class="me-1">
                            <Icon :icon="IconTypes.XMark" :displayStyle="DisplayStyle.Light" />
                        </span>
                        Client and Server Clocks are not synced
                    </MessageBadge>
                </span>
            </div>
        </div>
    </div>
</template>
